import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { cliente } from '@/lib/cliente';
import { cn } from '@/lib/utils';
import type { EstadoPinLabResponse, RespuestaApi } from '@adelante/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, KeyRound, Save } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

/**
 * `/admin/lab-pin` — Pantalla para que un Admin configure o rote el PIN
 * de acceso de laboratorio.
 *
 * Mostramos el estado actual (configurado/no, último uso, intentos
 * fallidos) sin exponer el PIN ni el hash. Para rotar, el admin tipea
 * 4 dígitos + 4 dígitos de confirmación.
 *
 * Si se cambia el PIN, las sesiones lab ACTUALES siguen válidas hasta
 * que su JWT expire (los JWTs no se invalidan instantáneamente). Esto
 * es OK porque solo hay 1 laboratorista y el flujo normal es: cambiás
 * el PIN cuando rota el personal.
 */

function formatearFecha(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-CR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function AdminLabPinPantalla() {
  const qc = useQueryClient();

  const consulta = useQuery<RespuestaApi<EstadoPinLabResponse>>({
    queryKey: ['admin', 'lab-pin', 'estado'],
    queryFn: () => cliente.adminLabPin.estado(),
  });

  const estado = consulta.data?.data ?? null;

  const [pin, setPin] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [etiqueta, setEtiqueta] = useState('Laboratorio');

  const configurar = useMutation<
    RespuestaApi<EstadoPinLabResponse>,
    Error,
    { pin: string; etiqueta: string }
  >({
    mutationFn: (body) => cliente.adminLabPin.configurar(body),
    onSuccess: (r) => {
      if (r.data) {
        toast.success('PIN configurado correctamente');
        setPin('');
        setPinConfirm('');
        qc.invalidateQueries({ queryKey: ['admin', 'lab-pin', 'estado'] });
      } else {
        toast.error(r.error?.mensaje ?? 'Error al configurar el PIN');
      }
    },
    onError: (e) => {
      toast.error('No se pudo configurar el PIN', { description: e.message });
    },
  });

  const pinValido = /^\d{4}$/.test(pin);
  const confirmValido = pin === pinConfirm && pinValido;
  const puedeGuardar = pinValido && confirmValido && !configurar.isPending;

  const onSubmit = () => {
    if (!puedeGuardar) return;
    configurar.mutate({ pin, etiqueta: etiqueta.trim() || 'Laboratorio' });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">PIN de acceso de Laboratorio</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Para usuarios que no usan correo (laboratoristas, operarios). El laboratorista entra a{' '}
          <code className="rounded bg-muted px-1 text-xs">/lab-login</code> con este PIN.
        </p>
      </div>

      {/* Estado actual */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Estado actual</CardTitle>
        </CardHeader>
        <CardContent>
          {consulta.isLoading && <Skeleton className="h-16 w-full" />}
          {consulta.error && (
            <div className="text-sm text-destructive">
              Error al cargar el estado: {consulta.error.message}
            </div>
          )}
          {estado && !estado.configurado && (
            <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
              <div className="text-sm text-amber-900">
                <div className="font-medium">No hay PIN configurado todavía.</div>
                <div className="mt-0.5 text-xs">
                  Configurá uno acá abajo y compartiselo al laboratorista. Sin PIN, no puede entrar
                  a la app.
                </div>
              </div>
            </div>
          )}
          {estado?.configurado && (
            <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
              <Campo label="Estado">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-900">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Activo
                </span>
              </Campo>
              <Campo label="Etiqueta">{estado.etiqueta ?? 'Laboratorio'}</Campo>
              <Campo label="Configurado por">{estado.creado_por_email ?? '—'}</Campo>
              <Campo label="Configurado el">{formatearFecha(estado.creado_en)}</Campo>
              <Campo label="Último uso">{formatearFecha(estado.ultimo_uso_en)}</Campo>
              <Campo label="Intentos fallidos recientes">
                {estado.intentos_fallidos > 0 ? (
                  <span className="text-amber-700">{estado.intentos_fallidos}</span>
                ) : (
                  <span className="text-muted-foreground">0</span>
                )}
              </Campo>
              {estado.bloqueado_hasta && (
                <Campo label="Bloqueado hasta" className="sm:col-span-2">
                  <span className="text-destructive">{formatearFecha(estado.bloqueado_hasta)}</span>
                </Campo>
              )}
            </dl>
          )}
        </CardContent>
      </Card>

      {/* Form configurar / rotar */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">
            {estado?.configurado ? 'Rotar PIN' : 'Configurar nuevo PIN'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-1.5">
            <Label htmlFor="etiqueta">Etiqueta (visible en la pantalla de login)</Label>
            <Input
              id="etiqueta"
              value={etiqueta}
              onChange={(e) => setEtiqueta(e.target.value)}
              maxLength={100}
              placeholder="Laboratorio"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor="pin">
                PIN nuevo (4 dígitos) <span className="text-destructive">*</span>
              </Label>
              <Input
                id="pin"
                type="password"
                inputMode="numeric"
                pattern="\d{4}"
                maxLength={4}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="••••"
                className="text-center font-mono text-2xl tracking-widest"
                autoComplete="new-password"
              />
              <p className="text-xs text-muted-foreground">Solo números, exactamente 4 dígitos.</p>
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="pin-confirm">
                Confirmar PIN <span className="text-destructive">*</span>
              </Label>
              <Input
                id="pin-confirm"
                type="password"
                inputMode="numeric"
                pattern="\d{4}"
                maxLength={4}
                value={pinConfirm}
                onChange={(e) => setPinConfirm(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="••••"
                className={cn(
                  'text-center font-mono text-2xl tracking-widest',
                  pinConfirm.length === 4 &&
                    !confirmValido &&
                    'border-destructive focus-visible:ring-destructive',
                )}
                autoComplete="new-password"
              />
              {pinConfirm.length === 4 && !confirmValido && (
                <p className="text-xs text-destructive">Los PINs no coinciden.</p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 border-t pt-4">
            <Button onClick={onSubmit} disabled={!puedeGuardar} className="gap-1.5">
              {estado?.configurado ? (
                <KeyRound className="h-4 w-4" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {configurar.isPending
                ? 'Guardando…'
                : estado?.configurado
                  ? 'Rotar PIN'
                  : 'Configurar PIN'}
            </Button>
            {estado?.configurado && (
              <p className="text-xs text-muted-foreground">
                Al rotar, el PIN anterior deja de funcionar inmediatamente. Las sesiones activas del
                laboratorista expiran en su próximo refresh (~30 días máximo).
              </p>
            )}
          </div>

          <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
            <div className="mb-1 font-medium">Para entregarle al laboratorista:</div>
            <ul className="ml-5 list-disc">
              <li>
                URL: <code className="bg-white px-1">{window.location.origin}/lab-login</code>
              </li>
              <li>PIN: el que acabás de configurar (4 dígitos).</li>
              <li>Recordá: solo hay UN PIN activo a la vez.</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Campo({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm">{children}</dd>
    </div>
  );
}
